import {
  workplaceProfileResponseSchema,
  workplaceProfileUpdateSchema,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { patch } from "@src/appV2/api/parsedApi";
import { environmentConfig } from "@src/appV2/environment";
import { getWorkplaceQueryKey } from "@src/appV2/FacilityProfile/api/useGetWorkplace";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface InvalidContractWarningSettingsPayload {
  enabled: boolean;
  contractFixDeadline?: string;
  contractSigningUrl?: string;
}

interface UseUpdateWorkplaceInvalidContractWarningParams {
  workplaceId: string;
  invalidContractWarningSettings: InvalidContractWarningSettingsPayload;
}

export function useUpdateWorkplaceInvalidContractWarning() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workplaceId,
      invalidContractWarningSettings,
    }: UseUpdateWorkplaceInvalidContractWarningParams) => {
      const url = `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}`;

      const { enabled, contractFixDeadline, contractSigningUrl } = invalidContractWarningSettings;

      const response = await patch({
        url,
        bodySchema: workplaceProfileUpdateSchema,
        responseSchema: workplaceProfileResponseSchema,
        data: {
          data: {
            type: "workplace" as const,
            id: workplaceId,
            attributes: {
              invalidContractWarningSettings: {
                enabled,
                ...(isDefined(contractFixDeadline) && { contractFixDeadline }),
                ...(isDefined(contractSigningUrl) && { contractSigningUrl }),
              },
            },
          },
        },
      });

      return response.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [getWorkplaceQueryKey(variables.workplaceId)],
      });
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.UPDATE_WORKPLACE_INVALID_CONTRACT_WARNING_FAILURE,
      userErrorMessage:
        "Error updating invalid contract warning settings. Please try again later or contact support.",
    },
  });
}
